import withStyles from '@mui/styles/withStyles';
import {color, font, type} from "common/tokens";
import React, {useEffect, useState} from "react";
import * as propTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { chatTimeParts, isRelativeTime } from "business/chatMessageGroup";
import VideoTypeMessage from "components/Chat/VideoTypeMessage";
import { truncateFileName, getFileExtensionFromName } from "common/helpers";
import { isEmpty, get } from "lodash";
import FileUploadAlt from 'common/assets/FileUploadAlt.png';
import DocFile from 'common/assets/DocFile.svg';
import XlsFile from 'common/assets/XlsFile.svg';
import PptFile from 'common/assets/PptFile.svg';
import PdfFile from 'common/assets/PdfFile.png';

const styles = () => ({
  wrapper: {
    display: 'flex',
    margin: '8px 0px',
    flexShrink: 0,
    '&$wrapperContinues': {
      marginTop: 2,
    },
    '&:hover $hoverTime': {
      opacity: 1,
    },
  },
  // Their messages sit on the muted surface, mine take the accent — the pair
  // reads as a conversation without needing the names on every line.
  wrapperContinues: {},
  messageContentWrapper: {
    ...type.body,
    background: color.SURFACE_MUTED,
    color: color.TEXT,
    padding: '10px 14px',
    maxWidth: '100%',
    // Wide enough that the sender's name and the time stay on one line instead
    // of stacking above a two-word message.
    minWidth: 200,
    position: 'relative',
    marginLeft: 8,
    marginRight: 8,
    borderRadius: 14,
  },
  // A note from Mentor Together itself, not a person in the group.
  isBroadcastMessageColor: {
    backgroundColor: color.ACCENT_TINT,
    color: color.TEXT,
  },
  // The corner nearest the speaker is squared off, so a run of messages still
  // points at who sent it.
  messageContainerWrapperForCurrentUser: {
    borderBottomRightRadius: 4,
  },
  messageContainerWrapperForOtherUser: {
    borderBottomLeftRadius: 4,
  },
  messageTime: {
    ...type.chip,
    fontWeight: font.REGULAR,
    color: color.TEXT_SUBTLE,
  },
  name: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    color: color.TEXT_MUTED,
    marginBottom: 4,
    marginRight: 4,
  },
  messageMeta: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    margin: '0 10px 4px',
  },
  nameAbove: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
  },
  metaTime: {
    ...type.chip,
    fontWeight: font.REGULAR,
    color: color.TEXT_SUBTLE,
  },
  // Holds the indent a continuation message would lose without its avatar.
  avatarSpacer: {
    display: 'block',
    width: 28,
    height: 1,
  },
  bubbleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  // Own messages are right aligned, so their time sits to the left of the
  // bubble and everyone else's to the right — always on the outside.
  bubbleRowSelf: {
    '& $hoverTime': {
      order: 0,
    },
  },
  hoverTime: {
    ...type.chip,
    fontWeight: font.REGULAR,
    color: color.TEXT_SUBTLE,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 120ms ease-out',
    order: 1,
  },
  avatarWrapper: {
    padding: '0 4px',
  },
  seenAvatar: {
    padding: '0 2px',
  },
  seenAvatarContainer: {
    display: "flex",
    flexShrink: 0,
    position: "absolute",
    right: 10,
    bottom: -10,
  },
  receiptAvatar: {
    width: 16,
    height: 16,
  },
  messageContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '72%',
    position: 'relative',
  },
  // The container already aligns its children to the right, so the time sits on
  // the bubble's own edge rather than across the pane from it.
  alignRight: {
    alignItems: 'flex-end',
  },
  justifyRight: {
    justifyContent: 'flex-end',
  },
  justifyLeft: {
    justifyContent: 'flex-start'
  },
  smallAvatar: {
    width: 28,
    height: 28
  },
  messageText: {
    fontSize: 12,
    lineHeight: '14px',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-line',
    marginBottom: 8,
  },
  messageAttachmentName: {
    fontWeight: '500',
    fontSize: 11,
    color: 'black',
    marginRight: 3
  },
  messageAttachmentExtension: {
    fontWeight: '700',
    fontSize: 11,
    color: '#E0B334',
    display: 'flex',
    alignItems: 'center',
  },
  messageAttachmentNameContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap'
  },
  attachmentDownloadIcon: {
    fontSize: 15,
    marginLeft: 2.5,
    cursor: 'pointer'
  },
  imagePreview: {
    maxWidth: 200,
    width: "100%",
    marginTop: 8,
    cursor: 'pointer'
  },
  // Own messages take the light accent tint, theirs the neutral surface — the
  // usual chat pairing. A saturated fill on every one of your own lines shouts.
  sendersBackground: {
    backgroundColor: color.ACCENT_TINT,
    color: color.TEXT,
  },
  overlayText: {
    textAlign: 'center',
    fontSize: '10px',
    fontWeight: 400,
  },
  overlayContainer: {
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    backgroundColor: '#fff',
    borderRadius: 40,
    position: 'absolute',
    bottom: 20,
    right: 0,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  },
  userProfile: {
    position: 'relative',
  },
  userTaged: {
    fontWeight: '400px',
    color: '#CFA222',
    lineHeight: '120%',
  },
  currentUserTag: {
    fontWeight: '600px',
    color: '#CFA222',
    lineHeight: '120%',
  },
  selectedMessage: {
    border: '1px solid #515DC0'
  },
  assistantMessageContentWrapper: {
    backgroundColor: color.ACCENT_TINT,
  },
  assistantMessageText: {
    color: color.TEXT,
  },
  messageDivider: {
    border: 0,
    height: '1px',
    backgroundColor: color.DIVIDER,
    margin: '10px 0'
  },
  helperText: {
    ...type.chip,
    color: color.TEXT_MUTED,
  },
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    background: color.SURFACE,
    border: `1px solid ${color.CARD_BORDER}`,
    borderRadius: 10,
    cursor: 'pointer',
    '&:hover': {
      borderColor: color.ACCENT,
    },
  },
  attachmentFileIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    flexShrink: 0,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
  },
  attachmentLabel: {
    ...type.meta,
    color: color.TEXT,
    flex: 1,
  },
  attachmentType: {
    ...type.chip,
    color: color.TEXT_SUBTLE,
  },
  attachmentDownload: {
    fontSize: 18,
    color: color.TEXT_MUTED,
  },
  // Only the time is left in here now the name sits above the bubble, so it
  // tucks into the top-right corner rather than taking a line of its own.
  messageHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 2,
  },
  linkPreviewContainer: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 10,
    cursor: 'pointer',
    backgroundColor: '#fff',
    alignItems: 'center'
  },
  linkPreviewImage: {
    width: 70,
    height: 70,
    objectFit: 'cover'
  },
  linkPreviewData: {
    padding: 10,
    flex: 1
  },
  linkPreviewTitle: {
    fontWeight: 'bold',
    fontSize: 12,
    color: '#333'
  },
  linkPreviewDescription: {
    fontSize: 10,
    color: '#666',
    marginTop: 4,
  },
});

const TextTypeMessage = ({
  classes,
  messageContent,
  messageDate,
  userName,
  myId,
  handleOpenCalender,
  isAIChannel,
  isSelf,
  isBroadcastMessage,
  messageAttachments
}) => {
  const renderParsedMessage = () => {
    const mentionRegex = /@\[([^]+?)\]\((\d+)\)/g;
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    const calendarRegex = /\{\{calendar\}\}/g;

    let parsedMessage = messageContent.trim().replace(/^"(.*)"$/, "$1");

    const unicodeToChar = (text) =>
      text.replace(/\\u([\dA-Fa-f]{4})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      );

    const decodeHtmlEntities = (text) => {
      const txt = document.createElement("textarea");
      txt.innerHTML = text;
      return txt.value;
    };

    parsedMessage = unicodeToChar(parsedMessage);
    parsedMessage = parsedMessage.replace(/\\r\\n/g, "");
    parsedMessage = decodeHtmlEntities(parsedMessage);

    let parsedData;
    while ((parsedData = mentionRegex.exec(parsedMessage)) !== null) {
      const mentionText = parsedData[1];
      const userId = parsedData[2];
      const isMyIdTagged = Number(userId) === myId;
      const fontWeight = isMyIdTagged ? "600" : "400";

      const replacement = `<span style="color: #CFA222; line-height: 120%; font-weight: ${fontWeight};">@${mentionText}</span>`;
      parsedMessage = parsedMessage.replace(parsedData[0], replacement);
    }

    const isMessageContainsCalendarLink = parsedMessage.includes("{{calendar}}");
    parsedMessage = parsedMessage.replace(calendarRegex, () => {
      return '<span class="calendar-link" style="color: #0B57D0; font-weight:600; cursor: pointer; text-decoration: underline;">📆 Click to add to the calendar</span>';
    });

    parsedMessage = parsedMessage.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #0B57D0; text-decoration: underline;">${url}</a>`;
    });

    return (
      <div
        dangerouslySetInnerHTML={{ __html: parsedMessage }}
        onClick={(e) => {
          if (
            isMessageContainsCalendarLink &&
            e.target.classList.contains("calendar-link")
          ) {
            handleOpenCalender();
          }
        }}
      />
    );
  };

  return (
    <>
      {!isEmpty(messageContent) && (
        <>
          <div
            className={classNames(
              classes.messageText,
              ((isAIChannel && !isSelf) || isBroadcastMessage) &&
                classes.assistantMessageText
            )}
          >
            {renderParsedMessage()}
          </div>
          {isBroadcastMessage && isEmpty(messageAttachments) && (
            <>
              <hr className={classes.messageDivider} />
              <div className={classes.helperText}>
                Tap on the group name for previous transcripts and summaries
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};


const isJson = (str) => {
  try {
    JSON.parse(str)
  } catch (e) {
    return false;
  }
  return isNaN(parseInt(str));
};

const handleDownloadImage = (file_url) => {
  window.open(file_url);
}

// A recent message counts up in place ("now", "34 min"); once it is an hour
// old the label settles on the clock and the ticking stops. Mirrors the
// Android app's message timestamps.
const MessageTime = ({className, timestamp}) => {
  const {t} = useTranslation();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!isRelativeTime(timestamp, now)) return undefined;
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, [timestamp, now]);

  const parts = chatTimeParts(timestamp, now);
  if (parts.kind === "now") return <span className={className}>{t("chatTimeNow")}</span>;
  if (parts.kind === "minutes") return <span className={className}>{t("chatTimeMinutes", {count: parts.minutes})}</span>;
  return <span className={className}>{parts.text}</span>;
};

const Message = (props) => {
  const {classes, messageContent, messageStatus, messageAttachments, isSelf,  onClickImage, displayPicture, membersData, isSelected, isAIChannel, isNewMessageLoading, setIsNewMessageLoading, reverseOrderedMessageLength, isBroadcastMessage, botUsers, message, } = props;

  const [hoveredUserId, setHoveredUserId] = useState(null);
  const handleUserHover = (userId) => {
    setHoveredUserId(userId);
  };

  useEffect(() => {
    if (isSelf && isNewMessageLoading && isAIChannel) {
      setIsNewMessageLoading(false);
    }
  }, [isSelf, reverseOrderedMessageLength]);

  const handleUserLeave = () => {
    setHoveredUserId(null);
  };

  return (
    <>
      <div>
        <div className={classNames(classes.wrapper, props.continuesRun && classes.wrapperContinues, isSelf ? classes.justifyRight : classes.justifyLeft)}>
          {!isSelf &&
            <div className={classes.avatarWrapper}>
              {props.continuesRun
                ? <span className={classes.avatarSpacer}/>
                : <Avatar src={displayPicture} className={classes.smallAvatar}/>}
            </div>
          }
          <div className={classNames(classes.messageContentContainer, isSelf && classes.alignRight)}>
            {/* Who is speaking and when, above the bubble — the bubble holds
                only what they said. A continuation of the same person's run
                shows neither; its time is on hover instead. */}
            {!props.continuesRun && (
              <div className={classes.messageMeta}>
                {!isSelf && Boolean(props.userName) && (
                  <span className={classes.nameAbove}>{props.userName}</span>
                )}
                <MessageTime className={classes.metaTime} timestamp={props.messageDate}/>
              </div>
            )}
            <div className={classNames(classes.bubbleRow, isSelf && classes.bubbleRowSelf)}>
            {/* The time is only worth the space when it is asked for, so it
                waits outside the bubble until the message is hovered. */}
            {props.continuesRun && (
              <MessageTime className={classes.hoverTime} timestamp={props.messageDate}/>
            )}
            <div className={classNames(classes.messageContentWrapper, ((isAIChannel && !isSelf) || (isBroadcastMessage)) && classes.assistantMessageContentWrapper,  isSelf ? classes.messageContainerWrapperForCurrentUser : classes.messageContainerWrapperForOtherUser, isSelf && classes.sendersBackground, isSelected && classes.selectedMessage, botUsers.some(bot => bot.userId === message.user_id) && classes.isBroadcastMessageColor )}>
              {isJson(messageContent) && !isEmpty(get(JSON.parse(messageContent), 'room'))
                ? <VideoTypeMessage {...props} classes={undefined}/>
                : <TextTypeMessage {...props}/>
              }
              {
                !isEmpty(messageAttachments) && (
                  <div>
                    {isBroadcastMessage ? (
                      <div className={classes.attachmentList}>
                        {messageAttachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className={classes.attachmentItem}
                            onClick={() => handleDownloadImage(attachment.presigned_url)}
                          >
                            <span className={classes.attachmentFileIcon}>
                              <DescriptionOutlinedIcon fontSize="small"/>
                            </span>
                            <span className={classes.attachmentLabel}>
                              {attachment.attachment_name.includes("summary") ? "Summary" : "Transcript"}
                            </span>
                            <span className={classes.attachmentType}>.txt</span>
                            <SaveAltIcon className={classes.attachmentDownload}/>
                          </div>
                        ))}
                      </div>
                    ) : (
                      messageAttachments.map(attachment => {
                        if (attachment.attachment_type === "link_preview") {
                          return (
                            <div
                              key={attachment.id}
                              className={classes.linkPreviewContainer}
                              onClick={() => window.open(attachment.url, '_blank')}
                            >
                              {attachment.thumbnail_presigned_url && (
                                <img
                                  src={attachment.thumbnail_presigned_url}
                                  alt="link preview"
                                  className={classes.linkPreviewImage}
                                />
                              )}
                              <div className={classes.linkPreviewData}>
                                <div className={classes.linkPreviewTitle}>{attachment.attachment_name}</div>
                                {attachment.description && (
                                  <div className={classes.linkPreviewDescription}>{attachment.description}</div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        const thumbnailAvailableForFiles = [
                          'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'pdf', 'xlsx', 'xls', 'ppt', 'pptx', 'doc', 'docx'
                        ];
                        const fileExtension = getFileExtensionFromName(attachment.attachment_name).toLowerCase();
                        if (!thumbnailAvailableForFiles.includes(fileExtension)) {
                          return <></>;
                        }

                        const isPdf = ['pdf'].includes(fileExtension);
                        const isGif = ['gif'].includes(fileExtension);
                        const isDoc = ['doc', 'docx'].includes(fileExtension);
                        const isXls = ['xls', 'xlsx'].includes(fileExtension);
                        const isPpt = ['ppt', 'pptx'].includes(fileExtension);

                        return (
                          attachment.thumbnail_presigned_url || attachment.url ? (
                            <img
                              alt="Preview not available"
                              className={classes.imagePreview}
                              src={isGif ? (attachment.url || attachment.presigned_url) : attachment.thumbnail_presigned_url}
                              key={attachment.id}
                              onClick={() =>
                                isPdf
                                  ? handleDownloadImage(attachment.presigned_url)
                                  : onClickImage(attachment.url || attachment.presigned_url)
                              }
                            />
                          ) : messageStatus === 'completed' ? (
                            (isDoc || isXls || isPpt || isPdf) ? (
                              <img
                                alt="Preview not available"
                                className={classes.imagePreview}
                                src={isDoc ? DocFile : isXls ? XlsFile : isPpt ? PptFile : PdfFile}
                                key={attachment.id}
                                onClick={() => handleDownloadImage(attachment.presigned_url)}
                              />
                            ) : (
                              <></>
                            )
                          ) : (
                            <img
                              alt="Preview not available"
                              className={classes.imagePreview}
                              src={FileUploadAlt}
                              key={attachment.id}
                            />
                          )
                        );
                      })
                    )}
                    {messageStatus === 'completed' ? (
                      <></>
                    ) : (
                      <LinearProgress />
                    )}
                    {messageAttachments.map(attachment => (
                      !attachment?.url && (
                        <div
                          className={classes.messageAttachmentNameContainer}
                          key={attachment.id}
                        >
                          <span className={classes.messageAttachmentName}>
                            {truncateFileName(attachment.attachment_name, 13)}
                          </span>
                          <span className={classes.messageAttachmentExtension}>
                            .{getFileExtensionFromName(attachment.attachment_name)}
                            {messageStatus === 'completed' && attachment.presigned_url && (
                              <SaveAltIcon
                                className={classes.attachmentDownloadIcon}
                                onClick={() => handleDownloadImage(attachment.presigned_url)}
                              />
                            )}
                          </span>
                        </div>
                      )
                    ))}
                  </div>
                )
              }
              {membersData.length > 0 && (
                <div className={classes.seenAvatarContainer}>
                  {membersData.map((member, index) => (
                    <div className={classes.userProfile} key={index}>
                      <div
                        className={classes.seenAvatar}
                        key={member.userId}
                        onMouseEnter={() => handleUserHover(member.userId)}
                        onMouseLeave={handleUserLeave}
                      >
                      <Avatar src={member.displayPicture} className={classes.receiptAvatar} />
                      </div>
                      {hoveredUserId === member.userId && (
                        <div className={classes.overlayContainer}>
                          <div className={classes.overlayText}>
                            {member.userFullName}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
};

export default withStyles(styles)(Message);

Message.propTypes = {
  messageContent: propTypes.string,
  messageDate: propTypes.string,
  isSelf: propTypes.bool,
  userName: propTypes.string
};

Message.defaultProps = {
  isSelf: true
};
